import { User } from './index';
import { PricingTier } from './b2b';

// Customer Type Enum
export enum CustomerType {
  INDIVIDUAL = 'individual',
  BUSINESS = 'business',
}

// Customer Type Labels for display
export const CustomerTypeLabels: Record<CustomerType, string> = {
  [CustomerType.INDIVIDUAL]: 'Individual',
  [CustomerType.BUSINESS]: 'Business',
};

// Customer Contact Interface (for business customers)
export interface CustomerContact {
  id: string;
  customerId: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  role?: string;
  isPrimary: boolean;
  createdAt: string;
  updatedAt: string;
}

// Customer Interface (unified individual and business)
export interface Customer {
  id: string;
  organizationId: string;
  type: CustomerType;
  // Common fields
  email: string;
  phone?: string;
  // Address fields
  street?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  // Geolocation fields
  latitude?: number;
  longitude?: number;
  // Delivery preferences
  preferDelivery: boolean;
  preferPickup: boolean;
  deliveryInstructions?: string;
  // Individual-specific fields (null for businesses)
  firstName?: string;
  lastName?: string;
  // Business-specific fields (null for individuals)
  companyName?: string;
  defaultTierId?: string;
  defaultTier?: PricingTier;
  isApproved: boolean;
  approvedById?: string;
  approvedBy?: User;
  // Activity tracking
  notes?: string;
  lastActivityAt?: string;
  lastActivityType?: string;
  totalOrders: number;
  totalOrderValue: number;
  totalQuotesRequested: number;
  // Relationships
  contacts?: CustomerContact[];
  // Audit fields
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string | null;
  // Legacy reference tracking (for migration)
  legacyUserId?: string;
  legacyResellerProfileId?: string;
}

// Create Contact DTO
export interface CreateContactDto {
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  role?: string;
  isPrimary?: boolean;
}

// Update Contact DTO
export interface UpdateContactDto {
  id?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  role?: string;
  isPrimary?: boolean;
}

// Create Customer DTO
export interface CreateCustomerDto {
  type: CustomerType;
  email: string;
  phone?: string;
  // Individual-specific fields (required for INDIVIDUAL)
  firstName?: string;
  lastName?: string;
  // Business-specific fields (required for BUSINESS)
  companyName?: string;
  defaultTierId?: string;
  contacts?: CreateContactDto[];
  isApproved?: boolean;
  // Address fields
  street?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  // Geolocation fields
  latitude?: number;
  longitude?: number;
  // Delivery preferences
  preferDelivery?: boolean;
  preferPickup?: boolean;
  deliveryInstructions?: string;
  // General
  notes?: string;
  isActive?: boolean;
}

// Update Customer DTO (type is immutable after creation)
export interface UpdateCustomerDto {
  email?: string;
  phone?: string;
  // Individual-specific fields
  firstName?: string;
  lastName?: string;
  // Business-specific fields
  companyName?: string;
  defaultTierId?: string;
  contacts?: UpdateContactDto[];
  isApproved?: boolean;
  // Address fields
  street?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  // Geolocation fields
  latitude?: number;
  longitude?: number;
  // Delivery preferences
  preferDelivery?: boolean;
  preferPickup?: boolean;
  deliveryInstructions?: string;
  // General
  notes?: string;
  isActive?: boolean;
}

// Customer Filters for list queries
export interface CustomerFilters {
  type?: CustomerType;
  isApproved?: boolean;
  isActive?: boolean;
  search?: string;
  page?: number;
  limit?: number;
}

// Customer Activity Stats Response
export interface CustomerActivityStats {
  customerId: string;
  totalOrders: number;
  totalOrderValue: number;
  totalQuotesRequested: number;
  lastActivityAt?: string;
  lastActivityType?: string;
}

// Customer Stats Response
export interface CustomerStats {
  total: number;
  individual: number;
  business: number;
  active: number;
  pendingApproval: number;
}

// Helper functions
export function getCustomerDisplayName(customer: Customer): string {
  if (customer.type === CustomerType.BUSINESS) {
    return customer.companyName || '';
  }
  return `${customer.firstName || ''} ${customer.lastName || ''}`.trim();
}

export function getCustomerFullName(customer: Customer): string {
  return `${customer.firstName || ''} ${customer.lastName || ''}`.trim();
}

export function isBusinessCustomer(customer: Customer): boolean {
  return customer.type === CustomerType.BUSINESS;
}

export function isIndividualCustomer(customer: Customer): boolean {
  return customer.type === CustomerType.INDIVIDUAL;
}

export function getContactFullName(contact: CustomerContact): string {
  return `${contact.firstName || ''} ${contact.lastName || ''}`.trim();
}

export function getPrimaryContact(contacts?: CustomerContact[]): CustomerContact | undefined {
  if (!contacts || contacts.length === 0) return undefined;
  return contacts.find(c => c.isPrimary) || contacts[0];
}
